import type React from 'react';
import {Box, Paper, Stack, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import type {PickQuadrant, ToolKey} from '../../../types/strategyTypes';
import type {PlanProps} from '../strategyProps';
import {PICK_COLORS} from '../strategyProps';
import {PlanSection} from '../PlanSection';
import {EditIn, HelpTitle} from '../SectionLinks';

const QUADRANTS: PickQuadrant[] = ['Implement', 'Challenge', 'Possible', 'Kill'];

const LEVERS_HELP = 'Levers are chosen under Strategy formulation: click moves on or off in the Strategy cube (solid tags) '
    + 'and sourcing options in the Sourcing wheel (outlined tags).';
const OPTIONS_HELP = 'The option cards are scored in Option appraisal: requirement weights × the 1-5 scores of each option, out '
    + 'of 100; the highest is preferred. The coloured row counts the PICK opportunities per group and their value.';

/**
 * Key commercial levers (from the cube and the wheel) and the three appraised options with the PICK summary. The
 * levers belong to the Strategy cube: the section goes when the cube is turned off.
 */
export const LeversAndOptionsSection: React.FC<PlanProps & {openTool: (key: ToolKey) => void}> = ({plan, money, openTool}) => {
    const {derived, content} = plan;
    const off = new Set(derived.toolsOff);
    const showLevers = !off.has('cube');
    const showOptions = !(off.has('appraisal') && off.has('pick'));
    return (
        <>
            {showLevers && <PlanSection
                title={<HelpTitle title="Key commercial levers" help={LEVERS_HELP}
                                  links={[{label: 'Strategy cube', onClick: () => openTool('cube')},
                                      ...(off.has('wheel') ? [] : [{label: 'Sourcing wheel', onClick: () => openTool('wheel')}])]}/>}
                actions={<EditIn label="Strategy cube" onClick={() => openTool('cube')}/>}
                edited={plan.edited.includes('cube') || plan.edited.includes('wheel')}>
                {derived.levers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No levers yet. Choose them in Strategy formulation, in the Strategy cube or the Sourcing wheel.</Typography>
                ) : (
                    <Stack direction="row" gap={1} flexWrap="wrap">
                        {derived.levers.map(l => <Tag key={l.label} label={l.label} solid={l.kind === 'cube'}
                                                      color={l.kind === 'cube' ? 'primary' : 'default'}/>)}
                    </Stack>
                )}
            </PlanSection>}

            {showOptions && <PlanSection
                title={<HelpTitle help={OPTIONS_HELP}
                                  links={[...(off.has('appraisal') ? [] : [{label: 'Option appraisal', onClick: () => openTool('appraisal')}]),
                                      ...(off.has('pick') ? [] : [{label: 'PICK', onClick: () => openTool('pick')}])]}
                                  title={off.has('pick') ? 'Options' : off.has('appraisal') ? 'Opportunities' : 'Opportunities and options'}/>}
                actions={(
                    <>
                        {!off.has('appraisal') && <EditIn label="Option appraisal" onClick={() => openTool('appraisal')}/>}
                        {!off.has('pick') && <EditIn label="PICK" onClick={() => openTool('pick')}/>}
                    </>
                )}
                edited={plan.edited.includes('appraisal') || plan.edited.includes('pick')}>
                <Stack gap={2}>
                    {!off.has('appraisal') && <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'}, gap: 1.5}}>
                        {content.appraisal.options.map((o, i) => {
                            const preferred = i === derived.appraisal.preferred;
                            return (
                                <Paper key={i} variant="outlined" sx={{p: 1.5, borderColor: preferred ? 'success.main' : undefined}}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
                                        <Typography variant="body2" fontWeight={600}>{o.name}</Typography>
                                        {preferred && <Tag size="small" label="Preferred" color="success"/>}
                                    </Stack>
                                    <Typography variant="h5">{`${derived.appraisal.scores[i]?.toFixed(0)}`}<Typography component="span" variant="body2" color="text.secondary"> /100</Typography></Typography>
                                </Paper>
                            );
                        })}
                    </Box>}
                    {!off.has('pick') && <Stack direction="row" gap={3} flexWrap="wrap" aria-label="PICK summary">
                        {QUADRANTS.map(q => (
                            <Stack key={q} direction="row" gap={1} alignItems="center">
                                <Box sx={{width: 10, height: 10, borderRadius: '50%', bgcolor: PICK_COLORS[q]}}/>
                                <Typography variant="body2">
                                    {`${q}: ${derived.pickSummary[q].count}`}
                                    <Typography component="span" variant="body2" color="text.secondary">
                                        {derived.pickSummary[q].value ? ` · ${money(derived.pickSummary[q].value)}` : ''}
                                    </Typography>
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>}
                </Stack>
            </PlanSection>}
        </>
    );
};
